// src/components/ui/choiceIllustration.tsx
// Shared tone/illustration helpers for the pictorial field types
// (`choice-matrix`, `pictorial-choice`) — promoted out of ChoiceMatrix.tsx
// once PictorialChoiceList.tsx needed the exact same mapping a second time,
// per this codebase's own "third reuse, promote it" convention.
import type { ReactNode } from 'react';
import { PlateFill, ActivityFigure, type IllustrationTone, type IllustrationSize } from './illustrations';

export const COLOR_TONE: Record<'green' | 'yellow' | 'red', IllustrationTone> = {
  green: 'success', yellow: 'warn', red: 'danger',
};
export const TONE_TEXT: Record<IllustrationTone, string> = {
  success: 'text-success-ink', warn: 'text-warn-ink', danger: 'text-danger-ink',
};
export const TONE_BORDER_BG: Record<IllustrationTone, string> = {
  success: 'border-success bg-success-soft',
  warn: 'border-warn bg-warn-soft',
  danger: 'border-danger bg-danger-soft',
};
/** A filled, high-contrast dot (radio indicator, checkmark backgrounds) —
 * distinct from `TONE_BORDER_BG`'s soft tint, which is for a whole
 * card/cell's selected state. */
export const TONE_SOLID: Record<IllustrationTone, string> = {
  success: 'bg-success text-white',
  warn: 'bg-warn text-white',
  danger: 'bg-danger text-white',
};

/** `illustration` names a Phase 0 component variant (`plate-1/3`, `plate-1/2`,
 * `plate-2/3`, `plate-full`, or `pose-walking`/`pose-lifting`/`pose-sitting`).
 * An unknown or missing name renders nothing — label alone, never a broken
 * glyph — per docs/HMS_V4_ASSESSMENT_FIELD_TYPES_SPEC.md's degradation table. */
export function renderChoiceIllustration(name: string | undefined, tone: IllustrationTone, size: IllustrationSize): ReactNode {
  if (!name) return null;
  if (name.startsWith('plate-')) {
    const fraction = name.slice(6);
    if (fraction === '1/3' || fraction === '1/2' || fraction === '2/3' || fraction === 'full') {
      return <PlateFill fraction={fraction} tone={tone} size={size} />;
    }
  }
  if (name.startsWith('pose-')) {
    const pose = name.slice(5);
    if (pose === 'walking' || pose === 'lifting' || pose === 'sitting') {
      return <ActivityFigure pose={pose} tone={tone} size={size} />;
    }
  }
  return null;
}
